import React from 'react';
import { Card, Descriptions, Table, Tag, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, Link } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';

interface StockAdjustmentItem {
  id: string;
  inventory_id: string | null;
  shelf_id: string | null;
  product_id: string;
  product_name: string;
  product_sku: string;
  quantity: number;
  reason_code: string | null;
  shelf_location: string | null;
  batch: string | null;
  expiration_date: string | null;
  manufacturing_date: string | null;
  inventory_type_id: string | null;
  inventory_type_name: string | null;
  custom_attributes: Record<string, unknown>;
  note: string | null;
}

interface StockAdjustment {
  id: string;
  name: string;
  organization_name: string;
  warehouse_name: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  note: string | null;
  created_at: string;
  applicant: { id: string; name: string } | null;
  approver: { id: string; name: string } | null;
  items: StockAdjustmentItem[];
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'approved': return 'success';
    case 'rejected': return 'error';
    case 'pending': return 'processing';
    default: return 'default';
  }
};

export default function AdminStockAdjustmentShow() {
  const adjustment = useLoaderData() as StockAdjustment;
  const { t } = useTranslation();

  const columns = [
    {
      title: t('models.product.name'),
      key: 'product',
      render: (_: unknown, r: StockAdjustmentItem) => (
        <Link to={`/products/${r.product_id}`}>
          <div>{r.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_name}</div>
        </Link>
      ),
    },
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
    },
    {
      title: t('models.inventory.inventory_type'),
      key: 'inventory_type',
      render: (_: unknown, r: StockAdjustmentItem) => r.inventory_type_name || '-',
    },
    {
      title: t('models.inventory.batch'),
      key: 'batch',
      render: (_: unknown, r: StockAdjustmentItem) => r.batch || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.stock_adjustment.quantity'),
      key: 'quantity',
      align: 'right' as const,
      render: (_: unknown, r: StockAdjustmentItem) => {
        const isGain = r.quantity > 0;
        const isLoss = r.quantity < 0;
        const color = isGain ? '#52c41a' : isLoss ? '#ff4d4f' : '#666';
        return (
          <span style={{ color, fontWeight: 'bold' }}>
            {isGain ? `+${r.quantity}` : r.quantity}
          </span>
        );
      },
    },
    {
      title: t('models.stock_adjustment.reason'),
      key: 'reason_code',
      render: (_: unknown, r: StockAdjustmentItem) =>
        r.reason_code ? t(`enums.stock_adjustment.reason_code.${r.reason_code}`, r.reason_code) : '-',
    },
    {
      title: t('models.stock_adjustment.note'),
      dataIndex: 'note',
      key: 'note',
      render: (v: string | null) => v || '-',
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <ListPageHeader
        basePath="/admin"
        items={['stocks', 'stocks.adjustments', { label: adjustment.name }]}
        title={
          <Space>
            {adjustment.name}
            <Tag color={getStatusColor(adjustment.status)}>
              {t(`enums.stock_adjustment.status.${adjustment.status}`)}
            </Tag>
          </Space>
        }
      />

      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Descriptions title={t('shared.stock_adjustment.basic_info')} bordered>
          <Descriptions.Item label={t('models.stock_adjustment.created_at')}>
            {adjustment.created_at}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.warehouse.name')}>
            {adjustment.warehouse_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.applicant')}>
            {adjustment.applicant?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.approver')}>
            {adjustment.approver?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.note')} span={3}>
            {adjustment.note || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('shared.stock_adjustment.items')} bordered={false}>
        <Table
          columns={columns}
          dataSource={adjustment.items}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
}
